import axios from 'axios'
import {
  ProjectCreateInput,
  ProjectDetail,
  ProjectListResponse,
  ProjectSummary,
  ResearchPaper,
  ResearchPaperCreate,
  ResearchPaperUpdate,
  ResearchPaperList,
  PaperMemberCreate,
  Document,

  DocumentList,
  DocumentChunk,
  Tag,
  TagCreate,
  AIArtifact,
  AIArtifactType,
  AIArtifactStatus,
  ProjectReferenceSuggestion,
  CitationSuggestion,
  CitationGraphData,
  ProjectDiscoveryPreferences,
  ProjectDiscoverySettingsPayload,
  ProjectDiscoveryRunResponse,
  ProjectDiscoveryResultsResponse,
  ProjectDiscoveryCountResponse,
  ProjectDiscoveryResultItem,
  ProjectDiscoveryClearResponse,
  ProjectSyncSession,
  ProjectSyncMessage,
  ProjectNotification,
  SyncSessionTokenResponse,
  MeetingSummary,
  AISummaryResponse,
  AIRephraseResponse,
  AIDocumentAnalysis,
  AIChatResponse,
  AIStatusResponse,
  AIOutlineResponse,
  AIKeywordsResponse,
  User,
  DiscussionMessage,
  DiscussionThread,
  DiscussionMessageCreate,
  DiscussionMessageUpdate,
  DiscussionStats,
  DiscussionChannelSummary,
  DiscussionChannelCreate,
  DiscussionChannelUpdate,
  DiscussionChannelResource,
  DiscussionChannelResourceCreate,
  DiscussionTask,
  DiscussionTaskCreate,
  DiscussionTaskUpdate,
  DiscussionTaskStatus,
  DiscussionAssistantRequest,
  DiscussionAssistantResponse,
  DiscussionAssistantHistoryItem,
  OpenRouterModel,
  OpenRouterModelsResponse,
  DiscoveryStreamEvent,
  DiscoveryStreamCompleteEvent,
  SupportFile,
  ConferenceTemplate,
} from '../types'

const deduceRuntimeOrigin = () => {
  if (typeof window !== 'undefined' && window?.location) {
    return window.location.origin
  }
  return ''
}

const rawApiRoot = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) || deduceRuntimeOrigin()
export const API_ROOT = rawApiRoot.replace(/\/$/, '')
export const API_BASE_URL = `${API_ROOT}/api/v1`.replace(/^\/api\//, '/api/')
export const buildApiUrl = (path: string) => {
  const sanitized = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE_URL}${sanitized}`
}

export const buildAuthHeaders = () => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  return headers
}

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL || '/api/v1',
  timeout: 180000, // 3 minutes for AI operations
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Track if we're currently refreshing tokens to avoid concurrent refreshes
let isRefreshing = false
let refreshPromise: Promise<string> | null = null
let failedQueue: Array<{
  resolve: (value?: any) => void
  reject: (error?: any) => void
}> = []

// Function to process the queue after token refresh
const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })

  failedQueue = []
}

// Internal function that actually performs the refresh
const doRefresh = async (): Promise<string> => {
  const response = await api.post<{ access_token: string; refresh_token?: string }>('/refresh')
  const { access_token } = response.data
  localStorage.setItem('access_token', access_token)
  return access_token
}

// Function to refresh token - ensures only one refresh happens at a time
export const refreshAuthToken = async (): Promise<string> => {
  // If already refreshing, wait for the existing refresh to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true

  refreshPromise = doRefresh()
    .then((token) => {
      processQueue(null, token)
      setupTokenRefreshTimer()
      return token
    })
    .catch((error) => {
      processQueue(error, null)
      localStorage.removeItem('access_token')
      localStorage.removeItem('user')
      throw error
    })
    .finally(() => {
      isRefreshing = false
      refreshPromise = null
    })

  return refreshPromise
}

const base64Decode = (input: string): string => {
  if (typeof atob === 'function') {
    return atob(input)
  }

  if (typeof globalThis !== 'undefined') {
    const maybeBuffer = (globalThis as unknown as {
      Buffer?: { from: (data: string, encoding: string) => { toString: (encoding: string) => string } }
    }).Buffer
    if (maybeBuffer) {
      return maybeBuffer.from(input, 'base64').toString('utf-8')
    }
  }

  throw new Error('No base64 decoder available')
}

const decodeJwtPayload = (token: string): Record<string, unknown> => {
  const parts = token.split('.')
  if (parts.length < 2) {
    throw new Error('Invalid JWT structure')
  }

  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
  const padding = (4 - (base64.length % 4 || 4)) % 4
  const padded = base64 + '='.repeat(padding)
  const decoded = base64Decode(padded)

  return JSON.parse(decoded)
}

// Proactive token refresh - refresh tokens 5 minutes before expiry
const setupTokenRefreshTimer = () => {
  const token = localStorage.getItem('access_token')
  if (!token) return

  if (!token.includes('.') || token.split('.').length < 3) {
    console.warn('Removing malformed access token from storage (token refresh timer)')
    localStorage.removeItem('access_token')
    return
  }

  try {
    const payload = decodeJwtPayload(token)
    const expValue = payload.exp
    const exp = typeof expValue === 'number' ? expValue : Number(expValue)
    if (!exp || Number.isNaN(exp)) {
      throw new Error('Missing exp claim')
    }

    const expiryTime = exp * 1000 // Convert to milliseconds
    const currentTime = Date.now()
    const timeUntilExpiry = expiryTime - currentTime
    const refreshTime = timeUntilExpiry - (5 * 60 * 1000) // 5 minutes before expiry

    if (refreshTime > 0) {
      setTimeout(async () => {
        try {
          await refreshAuthToken()
          console.log('Proactive token refresh successful')
          setupTokenRefreshTimer() // Set up next refresh
        } catch (error) {
          console.error('Proactive token refresh failed:', error)
        }
      }, refreshTime)
    }
  } catch (error) {
    console.error('Error setting up token refresh timer:', error)
    localStorage.removeItem('access_token')
  }
}

// Set up proactive refresh when page loads
if (typeof window !== 'undefined') {
  setupTokenRefreshTimer()
}

// Export the setup function for use after login
export { setupTokenRefreshTimer }

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      // Don't replace headers object, just add Authorization
      if (!config.headers) {
        config.headers = {}
      }
      config.headers.Authorization = `Bearer ${token}`
    } else if (config.headers && 'Authorization' in config.headers) {
      delete config.headers.Authorization
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors and auto-refresh tokens
type RetryableRequest = { _retry?: boolean; headers?: Record<string, unknown> } & Record<string, any>

api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = (error?.config || {}) as RetryableRequest

    // Handle subscription limit exceeded errors (402 Payment Required)
    if (error?.response?.status === 402 && error?.response?.data?.error === 'limit_exceeded') {
      // Dispatch custom event for UpgradeModal to catch
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('limit-exceeded', {
          detail: error.response.data
        }))
      }
      return Promise.reject(error)
    }

    // Handle email verification required errors (403 Forbidden)
    if (error?.response?.status === 403) {
      const detail = error?.response?.data?.detail || ''
      if (detail.includes('Email verification required')) {
        // Dispatch custom event for verification required
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('verification-required', {
            detail: { message: detail }
          }))
        }
        return Promise.reject(error)
      }
    }

    // Skip token refresh for auth endpoints (login, register, refresh, etc.)
    // IMPORTANT: /refresh must be here to prevent infinite loop when refresh token is invalid
    const authEndpoints = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/refresh']
    const requestUrl = originalRequest.url || ''
    const isAuthEndpoint = authEndpoints.some(endpoint => requestUrl.endsWith(endpoint))

    // Handle 403 that might be due to expired/invalid auth (race condition with token refresh)
    // If we get 403 "Project access denied" and we're already refreshing or just failed refresh, redirect to login
    if (error?.response?.status === 403 && !isAuthEndpoint) {
      const detail = error?.response?.data?.detail || ''
      const isAuthRelated = detail.includes('access denied') || detail.includes('Access denied')
      const noToken = !localStorage.getItem('access_token')

      if (isAuthRelated && (noToken || isRefreshing)) {
        const willRedirect = handleSessionExpired()
        if (willRedirect) return new Promise(() => {})
        // Already on an auth route — fall through and reject.
      }
    }

    if (error?.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      // No access token ever existed on this client — don't try to refresh.
      // This handles first-time visits (or logged-out users hitting protected
      // endpoints) where /refresh has no cookie to work with and would just
      // 401 again. Without this guard, a cold load on /login would loop.
      if (typeof window !== 'undefined' && !localStorage.getItem('access_token')) {
        return Promise.reject(error)
      }
      originalRequest._retry = true

      try {
        // refreshAuthToken handles deduplication - only one refresh happens even if called multiple times
        const newToken = await refreshAuthToken()

        ;(api.defaults.headers.common as Record<string, string>)['Authorization'] = `Bearer ${newToken}`
        originalRequest.headers = {
          ...(originalRequest.headers ?? {}),
          Authorization: `Bearer ${newToken}`,
        }

        // Retry the original request with new token
        return api(originalRequest as any)
      } catch (refreshError) {
        const willRedirect = handleSessionExpired()
        if (willRedirect) {
          // Return a never-resolving promise so the UI stays in loading state
          // instead of briefly flashing an error before the redirect.
          return new Promise(() => {})
        }
        // Already on an auth route — reject normally so callers can finish their
        // loading states instead of hanging forever.
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

// Shared session-expired handler: persists an explanation in sessionStorage so the
// login page can show a toast after the hard redirect, and preserves the attempted
// URL as a returnTo query param.
const SESSION_EXPIRED_FLAG = 'sh:session-expired'
const handleSessionExpired = (): boolean => {
  localStorage.removeItem('access_token')
  localStorage.removeItem('user')

  if (typeof window === 'undefined') return false

  const isAuthRoute =
    window.location.pathname.startsWith('/login') ||
    window.location.pathname.startsWith('/register') ||
    window.location.pathname.startsWith('/forgot-password') ||
    window.location.pathname.startsWith('/reset-password') ||
    window.location.pathname.startsWith('/verify-email')

  // Already on an auth route — don't redirect (would cause an infinite reload
  // loop when /refresh fails on cold page loads). Credentials are cleared above;
  // the existing page will re-render its unauthenticated state.
  if (isAuthRoute) return false

  try {
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, 'Your session has expired. Please sign in again.')
  } catch {
    // Private mode or quota failure — silently skip the toast.
  }

  const currentPath = window.location.pathname + window.location.search
  const returnTo = currentPath !== '/' ? `?returnTo=${encodeURIComponent(currentPath)}` : ''
  window.location.href = `/login${returnTo}`
  return true
}

export const consumeSessionExpiredMessage = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    const value = sessionStorage.getItem(SESSION_EXPIRED_FLAG)
    if (value) sessionStorage.removeItem(SESSION_EXPIRED_FLAG)
    return value
  } catch {
    return null
  }
}

// Auth API endpoints
export const authAPI = {
  register: (userData: {
    email: string
    password: string
    first_name?: string
    last_name?: string
  }) => api.post('/register', userData),

  login: (credentials: { email: string; password: string }) => {
    return api.post<{ access_token: string; refresh_token?: string }>('/login', {
      email: credentials.email,
      password: credentials.password
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
  },

  refresh: () => api.post<{ access_token: string; refresh_token?: string }>('/refresh'),

  logout: () => api.post('/logout'),

  getCurrentUser: () => api.get<User>('/me'),

  // Email verification
  verifyEmail: (token: string) =>
    api.post<{ message: string }>('/verify-email', { token }),

  resendVerification: (email: string) =>
    api.post<{ message: string }>('/resend-verification', { email }),

  // Password reset
  requestPasswordReset: (email: string) =>
    api.post<{ message: string }>('/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string) =>
    api.post<{ message: string }>('/reset-password', { token, new_password: newPassword }),

  // Google OAuth - returns the URL to redirect to
  getGoogleAuthUrl: () => `${API_BASE_URL}/google`,
}

// Users API endpoints
export const usersAPI = {
  getUsers: () => api.get('/users'),
  getUser: (id: string) => api.get(`/users/${id}`),
  updateUser: (id: string, userData: any) => api.put(`/users/${id}`, userData),
  changePassword: (passwordData: { current_password: string; new_password: string }) =>
    api.post('/change-password', passwordData),
  lookupByEmail: (email: string) =>
    api.get<{ id?: string; user_id?: string; userId?: string }>(`/users/lookup-by-email`, { params: { email } }),
  uploadAvatar: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post<User>('/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  deleteAvatar: () => api.delete<User>('/me/avatar'),
  // API Keys management
  getApiKeys: () => api.get<{
    openrouter: { configured: boolean; masked_key: string | null }
    zotero: { configured: boolean; masked_key: string | null; user_id: string | null }
  }>('/me/api-keys'),
  setOpenRouterKey: (apiKey: string | null) =>
    api.put<{ message: string; configured: boolean }>('/me/api-keys/openrouter', { api_key: apiKey }),
  setZoteroKey: (apiKey: string | null, userId: string | null) =>
    api.put<{ message: string; configured: boolean }>('/me/api-keys/zotero', { api_key: apiKey, user_id: userId }),
}

// Projects API endpoints
export const projectsAPI = {
  list: ({ skip = 0, limit = 50 }: { skip?: number; limit?: number } = {}) =>
    api.get<ProjectListResponse>('/projects/', { params: { skip, limit } }),

  create: (projectData: ProjectCreateInput) =>
    api.post<ProjectSummary>('/projects/', projectData),

  get: (projectId: string) => api.get<ProjectDetail>(`/projects/${projectId}`),

  update: (projectId: string, projectData: Partial<ProjectCreateInput>) =>
    api.put<ProjectSummary>(`/projects/${projectId}`, projectData),

  delete: (projectId: string) => api.delete(`/projects/${projectId}`),

  addMember: (projectId: string, payload: { user_id: string; role: string }) =>
    api.post(`/projects/${projectId}/members`, payload),

  inviteByEmail: (projectId: string, payload: { email: string; role: string }) =>
    api.post<{
      success: boolean;
      message: string;
      user_exists: boolean;
      member_id?: string;
      pending_invitation_id?: string;
    }>(`/projects/${projectId}/invite`, payload),

  getPendingInvitations: (projectId: string) =>
    api.get<Array<{
      id: string;
      email: string;
      project_id: string;
      role: string;
      invited_by?: string;
      created_at: string;
      expires_at?: string;
    }>>(`/projects/${projectId}/pending-invitations`),

  cancelPendingInvitation: (projectId: string, invitationId: string) =>
    api.delete(`/projects/${projectId}/pending-invitations/${invitationId}`),

  updateMember: (projectId: string, memberId: string, payload: { role: string }) =>
    api.patch(`/projects/${projectId}/members/${memberId}`, payload),

  removeMember: (projectId: string, memberId: string) =>
    api.delete(`/projects/${projectId}/members/${memberId}`),

  listPendingInvitations: () =>
    api.get<{ invitations: Array<{ project_id: string; project_title: string; member_id: string; role: string; invited_at?: string | null; invited_by?: string | null }> }>(
      '/projects/pending/invitations'
    ),

  acceptInvitation: (projectId: string, memberId: string) =>
    api.post(`/projects/${projectId}/members/${memberId}/accept`, null),

  declineInvitation: (projectId: string, memberId: string) =>
    api.post(`/projects/${projectId}/members/${memberId}/decline`, null),

  // Discussion AI Settings
  getDiscussionSettings: (projectId: string) =>
    api.get<{
      enabled: boolean
      model: string
      owner_has_api_key: boolean
      viewer_has_api_key: boolean
      server_key_available: boolean
      use_owner_key_for_team: boolean
    }>(`/projects/${projectId}/discussion-settings`),

  updateDiscussionSettings: (
    projectId: string,
    settings: { enabled?: boolean; model?: string; use_owner_key_for_team?: boolean }
  ) =>
    api.patch<{
      enabled: boolean
      model: string
      owner_has_api_key: boolean
      viewer_has_api_key: boolean
      server_key_available: boolean
      use_owner_key_for_team: boolean
    }>(`/projects/${projectId}/discussion-settings`, settings),

  // Objectives
  getObjectives: (projectId: string) =>
    api.get<{ completed_indices: number[] }>(`/projects/${projectId}/objectives`),

  updateObjectives: (projectId: string, completedIndices: number[]) =>
    api.patch<{ completed_indices: number[] }>(`/projects/${projectId}/objectives`, {
      completed_indices: completedIndices,
    }),

}

export const projectReferencesAPI = {
  listSuggestions: (projectId: string) =>
    api.get<{ project_id: string; suggestions: ProjectReferenceSuggestion[] }>(
      `/projects/${projectId}/references/suggestions`
    ),
  list: (projectId: string, params?: { status?: string }) =>
    api.get<{ project_id: string; references: ProjectReferenceSuggestion[] }>(
      `/projects/${projectId}/references`,
      { params: params?.status ? { status: params.status } : undefined }
    ),
  refreshSuggestions: (projectId: string) =>
    api.post<{ project_id: string; created: number; skipped: number }>(
      `/projects/${projectId}/references/suggestions/refresh`
    ),
  createSuggestion: (projectId: string, referenceId: string, confidence?: number) =>
    api.post<{ id: string; status: string }>(
      `/projects/${projectId}/references/suggestions`,
      null,
      {
        params: {
          reference_id: referenceId,
          confidence,
        },
      }
    ),
  approveSuggestion: (projectId: string, suggestionId: string, paperId?: string) =>
    api.post(
      `/projects/${projectId}/references/${suggestionId}/approve`,
      null,
      { params: paperId ? { paper_id: paperId } : undefined }
    ),
  rejectSuggestion: (projectId: string, suggestionId: string) =>
    api.post(`/projects/${projectId}/references/${suggestionId}/reject`, null),
  attachToPaper: (projectId: string, projectReferenceId: string, paperId: string) =>
    api.post(`/projects/${projectId}/references/${projectReferenceId}/attach`, {
      paper_id: paperId,
    }),
  detachFromPaper: (projectId: string, projectReferenceId: string, paperId: string) =>
    api.delete(`/projects/${projectId}/references/${projectReferenceId}/papers/${paperId}`),
  remove: (projectId: string, projectReferenceId: string) =>
    api.delete(`/projects/${projectId}/references/${projectReferenceId}`),
  listPaperReferences: (projectId: string, paperId: string) =>
    api.get<{ project_id: string; paper_id: string; references: any[] }>(
      `/projects/${projectId}/papers/${paperId}/references`
    ),
  getIngestionStatus: (projectId: string, referenceIds: string[]) =>
    api.post<{ statuses: Record<string, string> }>(
      `/projects/${projectId}/references/ingestion-status`,
      { reference_ids: referenceIds }
    ),
  suggestCitations: (projectId: string, text: string, limit?: number) =>
    api.post<{ suggestions: CitationSuggestion[] }>(
      `/projects/${projectId}/references/suggest-citations`,
      { text, limit: limit ?? 3 }
    ),
  importBibtex: (projectId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post<{ imported: number; skipped: number; errors: string[] }>(
      `/projects/${projectId}/references/import-bibtex`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
  },
  exportBibtex: (projectId: string) =>
    api.get(`/projects/${projectId}/references/export-bibtex`, {
      responseType: 'blob',
    }),
  getCitationGraph: (projectId: string) =>
    api.get<CitationGraphData>(`/projects/${projectId}/references/citation-graph`),
  getOrCreateChatChannel: (projectId: string, referenceId: string) =>
    api.post<{ channel_id: string; reference_id: string; reference_title: string; reference_status: string; is_new: boolean }>(
      `/projects/${projectId}/references/${referenceId}/chat-channel`
    ),
}

export const projectAIAPI = {
  listArtifacts: (
    projectId: string,
    params?: { status?: AIArtifactStatus; paperId?: string }
  ) =>
    api.get<{ project_id: string; artifacts: AIArtifact[] }>(
      `/projects/${projectId}/ai/artifacts`,
      {
        params: {
          status_filter: params?.status,
          paper_id: params?.paperId,
        },
      }
    ),
  generateArtifact: (
    projectId: string,
    payload: { type: AIArtifactType; paper_id?: string; focus?: string }
  ) => api.post(`/projects/${projectId}/ai/artifacts/generate`, payload),
}

export const projectDiscoveryAPI = {
  getSettings: (projectId: string) =>
    api.get<ProjectDiscoveryPreferences>(`/projects/${projectId}/discovery/settings`),
  updateSettings: (projectId: string, payload: ProjectDiscoverySettingsPayload) =>
    api.put<ProjectDiscoveryPreferences>(`/projects/${projectId}/discovery/settings`, payload),
  runDiscovery: (
    projectId: string,
    payload: {
      query?: string | null
      keywords?: string[] | null
      sources?: string[] | null
      max_results?: number | null
      relevance_threshold?: number | null
      auto_refresh_enabled?: boolean | null
      refresh_interval_hours?: number | null
    }
  ) =>
    api.post<ProjectDiscoveryRunResponse>(
      `/projects/${projectId}/discovery/run`,
      payload,
      { timeout: 180000 },
    ),
  listResults: (
    projectId: string,
    options?: { status?: string; skip?: number; limit?: number }
  ) =>
    api.get<ProjectDiscoveryResultsResponse>(
      `/projects/${projectId}/discovery/results`,
      {
        params: {
          status: options?.status,
          skip: options?.skip,
          limit: options?.limit,
        },
      }
    ),
  getPendingCount: (projectId: string, runType?: 'manual' | 'auto') =>
    api.get<ProjectDiscoveryCountResponse>(
      `/projects/${projectId}/discovery/results/pending/count`,
      {
        params: runType ? { run_type: runType } : undefined,
      }
    ),
  promoteResult: (projectId: string, resultId: string) =>
    api.post<ProjectDiscoveryResultItem>(
      `/projects/${projectId}/discovery/results/${resultId}/promote`,
      null,
    ),
  dismissResult: (projectId: string, resultId: string) =>
    api.post<ProjectDiscoveryResultItem>(
      `/projects/${projectId}/discovery/results/${resultId}/dismiss`,
      null,
    ),
  deleteResult: (projectId: string, resultId: string) =>
    api.delete<void>(
      `/projects/${projectId}/discovery/results/${resultId}`
    ),
  clearDismissedResults: (projectId: string) =>
    api.delete<ProjectDiscoveryClearResponse>(
      `/projects/${projectId}/discovery/results/dismissed`
    ),
  clearResults: (projectId: string, opts?: { runType?: 'manual' | 'auto'; status?: string }) =>
    api.delete<{ deleted: number }>(
      `/projects/${projectId}/discovery/results/clear`,
      {
        params: {
          ...(opts?.runType ? { run_type: opts.runType } : {}),
          ...(opts?.status ? { status: opts.status } : {}),
        },
      }
    ),
  runDiscoveryStream: async (
    projectId: string,
    payload: {
      query?: string | null
      keywords?: string[] | null
      sources?: string[] | null
      max_results?: number | null
      relevance_threshold?: number | null
    },
    onProgress: (event: DiscoveryStreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<DiscoveryStreamCompleteEvent | null> => {
    const url = buildApiUrl(`/projects/${projectId}/discovery/run-stream`)
    const headers = buildAuthHeaders()
    let response: Response
    try {
      response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        signal,
      })
    } catch (err) {
      if ((err as Error).name === 'AbortError') return null
      throw err
    }
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      throw new Error(`Discovery stream failed: ${response.status} ${errorText}`)
    }
    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('No response body')
    }
    const decoder = new TextDecoder()
    let buffer = ''
    let completeEvent: DiscoveryStreamCompleteEvent | null = null

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // Parse SSE lines
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const jsonStr = line.slice(6).trim()
          if (!jsonStr) continue
          try {
            const event = JSON.parse(jsonStr) as DiscoveryStreamEvent
            onProgress(event)
            if (event.type === 'complete') {
              completeEvent = event as DiscoveryStreamCompleteEvent
            }
          } catch {
            // Skip malformed JSON lines
          }
        }
      }
      // Flush remaining bytes from decoder
      buffer += decoder.decode()
      if (buffer.trim()) {
        const remaining = buffer.trim()
        if (remaining.startsWith('data: ')) {
          try {
            const event = JSON.parse(remaining.slice(6).trim()) as DiscoveryStreamEvent
            onProgress(event)
            if (event.type === 'complete') {
              completeEvent = event as DiscoveryStreamCompleteEvent
            }
          } catch {
            // Skip malformed final line
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return null
      throw err
    } finally {
      reader.releaseLock()
    }
    return completeEvent
  },
}

export const projectMeetingsAPI = {
  listMeetings: (projectId: string, options?: { status?: MeetingSummary['status'] }) =>
    api.get<{ project_id: string; meetings: MeetingSummary[] }>(
      `/projects/${projectId}/meetings`,
      {
        params: options?.status ? { status_filter: options.status } : undefined,
      }
    ),
  listSyncSessions: (projectId: string) =>
    api.get<{ project_id: string; sessions: ProjectSyncSession[] }>(
      `/projects/${projectId}/sync-sessions`
    ),
  createSyncSession: (
    projectId: string,
    payload: Partial<ProjectSyncSession>
  ) => api.post<ProjectSyncSession>(`/projects/${projectId}/sync-sessions`, payload),
  endSyncSession: (
    projectId: string,
    sessionId: string,
    payload: { status?: 'ended' | 'cancelled'; provider_payload?: Record<string, unknown> }
  ) => api.post<ProjectSyncSession>(`/projects/${projectId}/sync-sessions/${sessionId}/end`, payload),
  listSyncMessages: (projectId: string, sessionId: string) =>
    api.get<{ session: ProjectSyncSession; messages: ProjectSyncMessage[] }>(
      `/projects/${projectId}/sync-sessions/${sessionId}/messages`
    ),
  createSyncMessage: (
    projectId: string,
    sessionId: string,
    payload: { content: string; is_command?: boolean; role?: string | null; command?: string | null; metadata?: Record<string, unknown> | null }
  ) =>
    api.post<ProjectSyncMessage>(
      `/projects/${projectId}/sync-sessions/${sessionId}/messages`,
      payload
    ),
  attachRecording: (
    projectId: string,
    sessionId: string,
    payload: { audio_url?: string; summary?: string; action_items?: Record<string, unknown>; transcript?: Record<string, unknown> }
  ) =>
    api.post<MeetingSummary>(
      `/projects/${projectId}/sync-sessions/${sessionId}/recording`,
      payload
    ),
  uploadRecording: (projectId: string, sessionId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post<MeetingSummary>(
      `/projects/${projectId}/sync-sessions/${sessionId}/recording/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
  },
  createCallToken: (projectId: string, sessionId: string) =>
    api.post<SyncSessionTokenResponse>(
      `/projects/${projectId}/sync-sessions/${sessionId}/token`,
      {}
    ),
  deleteSyncSession: (projectId: string, sessionId: string) =>
    api.delete<void>(`/projects/${projectId}/sync-sessions/${sessionId}`),
}

export const projectNotificationsAPI = {
  listProjectNotifications: (projectId: string, options?: { unreadOnly?: boolean }) =>
    api.get<{ project_id: string; notifications: ProjectNotification[] }>(
      `/projects/${projectId}/notifications`,
      {
        params: {
          unread_only: options?.unreadOnly ?? false,
        },
      }
    ),
  markNotificationAsRead: (notificationId: string) =>
    api.post<ProjectNotification>(`/notifications/${notificationId}/read`, null),
}

// Project Discussion API endpoints
export const projectDiscussionAPI = {
  listMessages: (
    projectId: string,
    options?: { limit?: number; offset?: number; parentId?: string | null; channelId?: string | null }
  ) =>
    api.get<DiscussionMessage[]>(`/projects/${projectId}/discussion/messages`, {
      params: {
        limit: options?.limit ?? 100,
        offset: options?.offset ?? 0,
        parent_id: options?.parentId,
        channel_id: options?.channelId,
      },
    }),

  listThreads: (
    projectId: string,
    options?: { limit?: number; offset?: number; channelId?: string | null }
  ) =>
    api.get<DiscussionThread[]>(`/projects/${projectId}/discussion/threads`, {
      params: {
        limit: options?.limit ?? 50,
        offset: options?.offset ?? 0,
        channel_id: options?.channelId,
      },
    }),

  createMessage: (projectId: string, messageData: DiscussionMessageCreate) =>
    api.post<DiscussionMessage>(`/projects/${projectId}/discussion/messages`, messageData),

  updateMessage: (projectId: string, messageId: string, messageData: DiscussionMessageUpdate) =>
    api.put<DiscussionMessage>(`/projects/${projectId}/discussion/messages/${messageId}`, messageData),

  deleteMessage: (projectId: string, messageId: string) =>
    api.delete<void>(`/projects/${projectId}/discussion/messages/${messageId}`),

  getStats: (projectId: string, options?: { channelId?: string | null }) =>
    api.get<DiscussionStats>(`/projects/${projectId}/discussion/stats`, {
      params: {
        channel_id: options?.channelId,
      },
    }),

  listChannels: (projectId: string, options?: { includeArchived?: boolean }) =>
    api.get<DiscussionChannelSummary[]>(`/projects/${projectId}/discussion/channels`, {
      params: {
        include_archived: options?.includeArchived ?? false,
      },
    }),

  createChannel: (projectId: string, payload: DiscussionChannelCreate) =>
    api.post<DiscussionChannelSummary>(`/projects/${projectId}/discussion/channels`, payload),

  updateChannel: (projectId: string, channelId: string, payload: DiscussionChannelUpdate) =>
    api.put<DiscussionChannelSummary>(`/projects/${projectId}/discussion/channels/${channelId}`, payload),

  deleteChannel: (projectId: string, channelId: string) =>
    api.delete<void>(`/projects/${projectId}/discussion/channels/${channelId}`),

  listAssistantHistory: (projectId: string, channelId: string) =>
    api.get<DiscussionAssistantHistoryItem[]>(
      `/projects/${projectId}/discussion/channels/${channelId}/assistant-history`
    ),

  listChannelResources: (projectId: string, channelId: string) =>
    api.get<DiscussionChannelResource[]>(
      `/projects/${projectId}/discussion/channels/${channelId}/resources`
    ),

  createChannelResource: (
    projectId: string,
    channelId: string,
    payload: DiscussionChannelResourceCreate
  ) =>
    api.post<DiscussionChannelResource>(
      `/projects/${projectId}/discussion/channels/${channelId}/resources`,
      payload
    ),

  deleteChannelResource: (projectId: string, channelId: string, resourceId: string) =>
    api.delete<void>(
      `/projects/${projectId}/discussion/channels/${channelId}/resources/${resourceId}`
    ),

  listTasks: (
    projectId: string,
    options?: { channelId?: string | null; status?: DiscussionTaskStatus | null }
  ) =>
    api.get<DiscussionTask[]>(`/projects/${projectId}/discussion/tasks`, {
      params: {
        channel_id: options?.channelId,
        status_filter: options?.status,
      },
    }),

  createTask: (
    projectId: string,
    channelId: string,
    payload: DiscussionTaskCreate
  ) =>
    api.post<DiscussionTask>(
      `/projects/${projectId}/discussion/channels/${channelId}/tasks`,
      payload
    ),

  updateTask: (projectId: string, taskId: string, payload: DiscussionTaskUpdate) =>
    api.put<DiscussionTask>(`/projects/${projectId}/discussion/tasks/${taskId}`, payload),

  deleteTask: (projectId: string, taskId: string) =>
    api.delete<void>(`/projects/${projectId}/discussion/tasks/${taskId}`),

  listModels: (projectId: string, includeMeta: boolean = true) =>
    api.get<OpenRouterModel[] | OpenRouterModelsResponse>(
      `/projects/${projectId}/discussion/models`,
      { params: { include_meta: includeMeta } }
    ),

  invokeAssistant: (
    projectId: string,
    channelId: string,
    payload: DiscussionAssistantRequest
  ) =>
    api.post<DiscussionAssistantResponse>(
      `/projects/${projectId}/discussion/channels/${channelId}/assistant`,
      payload
    ),

  executePaperAction: (
    projectId: string,
    actionType: string,
    payload: Record<string, unknown>
  ) =>
    api.post<{ success: boolean; paper_id?: string; url_id?: string; reference_id?: string; message: string; ingestion_status?: string }>(
      `/projects/${projectId}/discussion/paper-action`,
      { action_type: actionType, payload }
    ),

  searchReferences: (
    projectId: string,
    query: string,
    options?: { sources?: string[]; maxResults?: number; openAccessOnly?: boolean }
  ) =>
    api.post<{
      papers: Array<{
        id: string
        title: string
        authors: string[]
        year?: number
        abstract?: string
        doi?: string
        url?: string
        source: string
        relevance_score?: number
        pdf_url?: string
        is_open_access?: boolean
      }>
      total_found: number
      query: string
    }>(`/projects/${projectId}/discussion/search-references`, {
      query,
      sources: options?.sources,
      max_results: options?.maxResults ?? 10,
      open_access_only: options?.openAccessOnly ?? false,
    }),

  // Batch search across multiple topics
  batchSearchReferences: (
    projectId: string,
    queries: Array<{ topic: string; query: string; max_results?: number }>,
    options?: { openAccessOnly?: boolean }
  ) =>
    api.post<{
      results: Array<{
        topic: string
        query: string
        papers: Array<{
          id: string
          title: string
          authors: string[]
          year?: number
          abstract?: string
          doi?: string
          url?: string
          source: string
          relevance_score?: number
          pdf_url?: string
          is_open_access?: boolean
        }>
        count: number
      }>
      total_found: number
    }>(`/projects/${projectId}/discussion/batch-search-references`, {
      queries: queries.map(q => ({
        topic: q.topic,
        query: q.query,
        max_results: q.max_results ?? 5,
      })),
      open_access_only: options?.openAccessOnly ?? false,
    }),

  // Channel Artifacts
  listArtifacts: (projectId: string, channelId: string) =>
    api.get<Array<{
      id: string
      title: string
      filename: string
      format: string
      artifact_type: string
      mime_type: string
      file_size?: string
      created_at: string
      created_by?: string
    }>>(`/projects/${projectId}/discussion/channels/${channelId}/artifacts`),

  getArtifact: (projectId: string, channelId: string, artifactId: string) =>
    api.get<{
      id: string
      title: string
      filename: string
      content_base64: string
      mime_type: string
    }>(`/projects/${projectId}/discussion/channels/${channelId}/artifacts/${artifactId}`),

  deleteArtifact: (projectId: string, channelId: string, artifactId: string) =>
    api.delete(`/projects/${projectId}/discussion/channels/${channelId}/artifacts/${artifactId}`),
}

// OpenRouter Agent API endpoints
export const openRouterAgentAPI = {
  listModels: (includeMeta: boolean = true) =>
    api.get<OpenRouterModel[] | OpenRouterModelsResponse>(
      '/agent-or/models',
      { params: { include_meta: includeMeta } }
    ),
}

// Research Papers API endpoints
export const researchPapersAPI = {
  createPaper: (paperData: ResearchPaperCreate) => 
    api.post<ResearchPaper>('/research-papers/', paperData),
  
  getPapers: (
    skipOrOptions: number | { skip?: number; limit?: number; projectId?: string } = 0,
    limit = 100
  ) => {
    let skip = 0
    let limitValue = limit
    let projectId: string | undefined

    if (typeof skipOrOptions === 'object' && skipOrOptions !== null) {
      skip = skipOrOptions.skip ?? 0
      limitValue = skipOrOptions.limit ?? limit
      projectId = skipOrOptions.projectId
    } else {
      skip = skipOrOptions
      limitValue = limit
    }

    const params = new URLSearchParams()
    params.set('skip', String(skip))
    params.set('limit', String(limitValue))
    if (projectId) params.set('project_id', projectId)

    return api.get<ResearchPaperList>(`/research-papers/?${params.toString()}`)
  },
  
  getPaper: (id: string) => 
    api.get<ResearchPaper>(`/research-papers/${id}`),
  
  updatePaper: (id: string, paperData: ResearchPaperUpdate) => 
    api.put<ResearchPaper>(`/research-papers/${id}`, paperData),
  
  deletePaper: (id: string) => 
    api.delete(`/research-papers/${id}`),
  
  // Enhanced content update with JSON support and versioning
  updatePaperContent: (
    id: string,
    contentData: {
      content?: string;
      content_json?: any;
      save_as_version?: boolean;
      version_summary?: string;
      manual_save?: boolean;  // True when user clicks save button (always creates snapshot)
    }
  ) => api.put(`/research-papers/${id}/content`, contentData),
  
  // Versioning endpoints
  getPaperVersions: (paperId: string) =>
    api.get(`/research-papers/${paperId}/versions`),
  
  getPaperVersion: (paperId: string, versionNumber: string) =>
    api.get(`/research-papers/${paperId}/versions/${versionNumber}`),
  
  createPaperVersion: (paperId: string, versionData: {
    version_number: string;
    title: string;
    content?: string;
    content_json?: any;
    abstract?: string;
    keywords?: string;
    references?: string;
    change_summary?: string;
  }) => api.post(`/research-papers/${paperId}/versions`, versionData),
  
  restorePaperVersion: (paperId: string, versionNumber: string) =>
    api.put(`/research-papers/${paperId}/versions/${versionNumber}/restore`),

  uploadFigure: (paperId: string, formData: FormData) =>
    api.post(`/research-papers/${paperId}/figures`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  uploadBib: async (paperId: string, formData: FormData) => {
    // Use the same approach as uploadFigure
    const response = await api.post(`/research-papers/${paperId}/upload-bib`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response
  },


  // Branch-based versioning (commits)
  // Helper to find or create a branch by name for a paper
  _getOrCreateBranchId: async (paperId: string, branchName: string): Promise<string> => {
    // List existing branches
    const listResp = await api.get<any[]>(`/branches/paper/${paperId}`)
    const branches: any[] = Array.isArray(listResp.data) ? listResp.data : []
    let found = branches.find(b => (b?.name || '').toLowerCase() === (branchName || '').toLowerCase())
    if (!found) {
      const createResp = await api.post<any>(`/branches/`, { name: branchName, paper_id: paperId })
      found = createResp.data
    }
    return found?.id
  },

  saveVersion: async (
    paperId: string,
    content: string,
    message: string,
    branchName = 'main',
    contentJson?: any,
    compileMeta?: { compilation_status?: 'success' | 'failed' | 'not_compiled'; pdf_url?: string; compile_logs?: string }
  ) => {
    const branchId = await researchPapersAPI._getOrCreateBranchId(paperId, branchName)
    const payload: any = { message: message || 'Update', content, content_json: contentJson }
    if (compileMeta) {
      if (compileMeta.compilation_status) payload.compilation_status = compileMeta.compilation_status
      if (compileMeta.pdf_url) payload.pdf_url = compileMeta.pdf_url
      if (compileMeta.compile_logs) payload.compile_logs = compileMeta.compile_logs
    }
    const resp = await api.post<any>(`/branches/${branchId}/commit`, payload)
    return resp.data
  },

  getVersionHistory: async (paperId: string, branchName = 'main', limit = 50, opts?: { state?: 'draft' | 'ready_for_review' | 'published' }) => {
    const branchId = await researchPapersAPI._getOrCreateBranchId(paperId, branchName)
    const q = new URLSearchParams()
    if (opts?.state) q.set('state', opts.state)
    const url = q.toString() ? `/branches/${branchId}/commits?${q}` : `/branches/${branchId}/commits`
    const resp = await api.get<any[]>(url)
    const commits = Array.isArray(resp.data) ? resp.data : []
    return commits.slice(0, limit)
  },

  updateCommitState: async (commitId: string, state: 'draft'|'ready_for_review'|'published') => {
    const resp = await api.put<any>(`/branches/commit/${commitId}`, { state })
    return resp.data
  },

  switchBranch: async (paperId: string, branchName = 'main') => {
    const branchId = await researchPapersAPI._getOrCreateBranchId(paperId, branchName)
    const resp = await api.post<Record<string, unknown>>(`/branches/${branchId}/switch`, { paper_id: paperId })
    // Also fetch head commit id
    const commitsResp = await api.get<any[]>(`/branches/${branchId}/commits`)
    const headCommit = Array.isArray(commitsResp.data) && commitsResp.data.length > 0 ? commitsResp.data[0] : null
    return { ...(resp.data as Record<string, unknown>), headCommit }
  },

  mergeBranches: async (sourceBranchId: string, targetBranchId: string, strategy: 'auto'|'manual' = 'auto') => {
    const resp = await api.post<any>(`/branches/merge`, { source_branch_id: sourceBranchId, target_branch_id: targetBranchId, strategy })
    return resp.data
  },
  
  // Team management
  addPaperMember: (paperId: string, memberData: PaperMemberCreate) => 
    api.post(`/research-papers/${paperId}/members`, memberData),

  // Invitations
  getPendingInvitations: () =>
    api.get<{ pending_invitations: Array<{ id: string; paper_id: string; role: string; invited_at: string; paper_title: string }> }>(
      '/research-papers/invitations/pending'
    ),

  // References
  addReference: (paperId: string, ref: {
    title: string;
    authors?: string[];
    year?: number;
    doi?: string;
    url?: string;
    source?: string;
    is_open_access?: boolean;
    pdf_url?: string;
  }) => api.post(`/research-papers/${paperId}/references`, ref),

  listReferences: (paperId: string) => api.get<{ references: any[]; total: number }>(`/research-papers/${paperId}/references`),

  deleteReference: (paperId: string, refId: string) => api.delete(`/research-papers/${paperId}/references/${refId}`),

  // Support files (images, .bst, .cls, etc.)
  uploadSupportFile: (paperId: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post<{ filename: string; size: number; type: string }>(
      `/research-papers/${paperId}/support-files`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
  },
  listSupportFiles: (paperId: string) =>
    api.get<{ files: SupportFile[] }>(`/research-papers/${paperId}/support-files`),
  deleteSupportFile: (paperId: string, filename: string) =>
    api.delete(`/research-papers/${paperId}/support-files/${encodeURIComponent(filename)}`),
  listFigures: (paperId: string) =>
    api.get<{ files: { filename: string; size: number }[] }>(`/research-papers/${paperId}/figures`),
  // User templates
  uploadTemplate: (paperId: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post<{ filename: string; size: number; name: string }>(
      `/research-papers/${paperId}/templates/upload`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
  },
  listUserTemplates: (paperId: string) =>
    api.get<{ templates: { filename: string; name: string; size: number; uploaded_at: string }[] }>(
      `/research-papers/${paperId}/templates`
    ),
  deleteUserTemplate: (paperId: string, filename: string) =>
    api.delete(`/research-papers/${paperId}/templates/${encodeURIComponent(filename)}`),
  getTemplateContent: (paperId: string, filename: string) =>
    api.get<string>(`/research-papers/${paperId}/templates/${encodeURIComponent(filename)}/content`),
}

// Documents API endpoints (for reference documents, not rich text editing)
export const documentsAPI = {
  // File upload endpoints for reference documents
  uploadDocument: (formData: FormData) => 
    api.post<Document>('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  
  getDocuments: (paperId?: string, skip = 0, limit = 100) => {
    const params = new URLSearchParams()
    if (paperId) params.append('paper_id', paperId)
    params.append('skip', skip.toString())
    params.append('limit', limit.toString())
    return api.get<DocumentList>(`/documents/list?${params.toString()}`)
  },
  
  getDocument: (id: string) => 
    api.get<Document>(`/documents/${id}`),
  
  createDocument: (documentData: any) => 
    api.post<Document>('/documents/', documentData),
  
  updateDocument: (id: string, documentData: any) => 
    api.put<Document>(`/documents/${id}`, documentData),
  
  deleteDocument: (id: string) => 
    api.delete(`/documents/${id}`),
  
  getDocumentChunks: (id: string, skip = 0, limit = 100) => 
    api.get<{ chunks: DocumentChunk[]; total: number }>(
      `/documents/${id}/chunks?skip=${skip}&limit=${limit}`
    ),
  
  analyzeDocument: (id: string) => 
    api.post<AIDocumentAnalysis>(`/documents/${id}/analyze`),
  
  chatWithDocument: (id: string, question: string) => 
    api.post<AIChatResponse>(`/documents/${id}/chat`, { question }),

  // Ingest a remote PDF by URL (OA PDFs recommended)
  ingestRemote: (formData: FormData) =>
    api.post<Document>(`/documents/ingest-remote`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
}


// Team API endpoints
export const teamAPI = {
  getTeamMembers: (paperId: string) => 
    api.get(`/team/papers/${paperId}/members`),
  
  inviteTeamMember: (paperId: string, email: string, role: string) => 
    api.post(`/team/papers/${paperId}/invite`, { email, role }),
  
  acceptInvitation: (paperId: string, memberId: string) => 
    api.post(`/team/papers/${paperId}/members/${memberId}/accept`),
  
  declineInvitation: (paperId: string, memberId: string) => 
    api.post(`/team/papers/${paperId}/members/${memberId}/decline`),
  
  removeTeamMember: (paperId: string, memberId: string) => 
    api.delete(`/team/papers/${paperId}/members/${memberId}`),

  updateMemberRole: (paperId: string, memberId: string, role: string) => 
    api.patch(`/team/papers/${paperId}/members/${memberId}`, { role }),
}

// AI API endpoints
export const aiAPI = {
  // === Reference-based AI endpoints (PRIMARY) ===
  // Main reference-based chat endpoint
  chatWithReferences: (query: string, paperId?: string | null) =>
    api.post<{
      response: string;
      sources: string[];
      sources_data: any[];
      chat_id: string;
      scope: string;
      paper_title?: string;
    }>('/ai/chat-with-references', { query, paper_id: paperId || null }),

  // Reference processing endpoints
  ingestReference: (referenceId: string, forceReprocess?: boolean) =>
    api.post<{ success: boolean; message: string; status: string }>(`/ai/references/${referenceId}/ingest`, {}, {
      params: { force_reprocess: forceReprocess || false }
    }),
    
  ingestPaperReferences: (paperId: string, forceReprocess?: boolean) =>
    api.post<{ success: boolean; message: string; total_references: number; processed: number; failed: number }>(`/ai/papers/${paperId}/ingest-references`, {}, {
      params: { force_reprocess: forceReprocess || false }
    }),
    
  // Reference status endpoints
  getReferenceChatStatus: (referenceId: string) =>
    api.get<{ reference_id: string; chat_ready: boolean; status: string; chunk_count: number; has_pdf: boolean }>(`/ai/references/${referenceId}/chat-status`),
    
  getPaperReferencesChatStatus: (paperId: string) =>
    api.get<{ 
      paper_id: string; 
      paper_title: string; 
      total_references: number; 
      chat_ready_references: number; 
      total_chunks: number; 
      overall_chat_ready: boolean;
      references: any[];
    }>(`/ai/papers/${paperId}/references/chat-status`),

  // === Legacy document-based endpoints (DEPRECATED - use reference-based instead) ===
  chatWithDocuments: (query: string) =>
    api.post<AIChatResponse>('/ai/chat-with-documents', { query }),

  processDocumentForAI: (documentId: string) =>
    api.post<{ success: boolean; message: string; document_id: string }>(`/ai/documents/${documentId}/process`),

  getDocumentChunks: (documentId: string) =>
    api.get<{ chunks: DocumentChunk[] }>(`/ai/documents/${documentId}/chunks`),

  getChatHistory: (limit: number = 20) =>
    api.get<{ chat_sessions: any[]; total_sessions: number }>(`/ai/chat-history?limit=${limit}`),

  // === General AI endpoints ===
  getAIStatus: () =>
    api.get<AIStatusResponse>('/ai/status'),

  getModelConfiguration: () =>
    api.get<any>('/ai/models'),

  updateModelConfiguration: (provider: string, embedding_model?: string, chat_model?: string) =>
    api.put<any>('/ai/models', { provider, embedding_model, chat_model }),

  // === AI Writing Tools endpoints ===
  generateText: (text: string, instruction: string, context?: string, maxLength = 500) =>
    api.post<any>('/ai/writing/generate', { 
      text, 
      instruction, 
      context, 
      max_length: maxLength 
    }),

  checkGrammarAndStyle: (text: string, checkGrammar = true, checkStyle = true, checkClarity = true) =>
    api.post<any>('/ai/writing/grammar-check', { 
      text, 
      check_grammar: checkGrammar, 
      check_style: checkStyle, 
      check_clarity: checkClarity 
    }),

  enhanceWithResearchContext: (text: string, paperIds: string[], queryType: string) =>
    api.post<any>('/ai/writing/research-context', { 
      text, 
      paper_ids: paperIds, 
      query_type: queryType 
    }),

  // === Legacy endpoints (keeping for backward compatibility) ===
  summarizeText: (text: string, maxLength = 150) => 
    api.post<AISummaryResponse>('/ai/summarize', { text, max_length: maxLength }),
  
  rephraseText: (text: string, style = 'academic') => 
    api.post<AIRephraseResponse>('/ai/rephrase', { text, style }),
  
  generateOutline: (topic: string, content?: string) => 
    api.post<AIOutlineResponse>('/ai/generate-outline', { topic, content }),
  
  extractKeywords: (text: string, maxKeywords = 10) => 
    api.post<AIKeywordsResponse>('/ai/extract-keywords', { 
      text, 
      max_keywords: maxKeywords 
    }),
}

export const streamAPI = {
  chatWithReferencesStream: async (query: string, paperId?: string | null, documentExcerpt?: string | null) => {
    const url = buildApiUrl('/ai/chat-with-references/stream')
    return fetch(url, {
      method: 'POST',
      headers: buildAuthHeaders(),
      body: JSON.stringify({ query, paper_id: paperId || null, document_excerpt: documentExcerpt || null }),
    })
  },
  writingGenerateStream: async (text: string, instruction: string, context?: string, maxLength = 500) => {
    const url = buildApiUrl('/ai/writing/generate/stream')
    return fetch(url, {
      method: 'POST',
      headers: buildAuthHeaders(),
      body: JSON.stringify({ text, instruction, context, max_length: maxLength }),
    })
  }
}

// Tags API endpoints
export const tagsAPI = {
  createTag: (tagData: TagCreate) => 
    api.post<Tag>('/tags/', tagData),
  
  getTags: () => 
    api.get<Tag[]>('/tags/'),
}

// LaTeX API
export const latexAPI = {
  exportDocx: (payload: {
    latex_source: string
    paper_id?: string
    latex_files?: Record<string, string>
    include_bibtex?: boolean
  }) =>
    api.post('/latex/export-docx', payload, {
      responseType: 'blob',
      timeout: 60000,
    }),
  exportSourceZip: (payload: {
    latex_source: string
    paper_id?: string
    latex_files?: Record<string, string>
    include_bibtex?: boolean
  }) =>
    api.post('/latex/export-source-zip', payload, {
      responseType: 'blob',
      timeout: 60000,
    }),
  analyzeWriting: (payload: { latex_source: string; paper_id?: string; venue?: string; latex_files?: Record<string, string> }) =>
    api.post<{
      issues: Array<{
        type: string
        severity: string
        message: string
        line: number | null
        suggestion: string | null
      }>
      stats: Record<string, number>
      score: number
    }>('/latex/analyze-writing', payload),
  getSubmissionVenues: () =>
    api.get<{
      venues: Record<string, {
        name: string
        abstract_max_words: number | null
        sections: string[] | null
        reference_style: string
        file_structure: string[]
        document_class: string
        required_packages: string[]
        page_limit: number | null
        font_size: string
      }>
    }>('/latex/submission-venues'),
  validateSubmission: (payload: { latex_source: string; venue: string; paper_id?: string }) =>
    api.post<{
      venue: string
      venue_name: string
      issues: Array<{
        type: string
        severity: string
        message: string
        passed: boolean
      }>
    }>('/latex/validate-submission', payload),
  buildSubmission: (payload: {
    latex_source: string
    venue: string
    paper_id?: string
    latex_files?: Record<string, string>
    include_bibtex?: boolean
  }) =>
    api.post('/latex/build-submission', payload, {
      responseType: 'blob',
      timeout: 60000,
    }),
  getTemplates: () =>
    api.get<{ templates: ConferenceTemplate[] }>('/latex/templates'),
}

export default api
// References Library API
export const referencesAPI = {
  listMy: (params?: { skip?: number; limit?: number; q?: string }) => {
    const p = new URLSearchParams()
    if (params?.skip) p.set('skip', String(params.skip))
    if (params?.limit) p.set('limit', String(params.limit))
    if (params?.q) p.set('q', params.q)
    return api.get<{ references?: any[] } | any[]>(`/references/?${p.toString()}`)
  },
  create: (payload: {
    title: string;
    authors?: string[];
    year?: number;
    doi?: string;
    url?: string;
    source?: string;
    journal?: string;
    abstract?: string;
    is_open_access?: boolean;
    pdf_url?: string;
    paper_id?: string;
  }) => api.post<{ reference?: { id?: string }; id?: string }>(`/references/`, payload),
  uploadPdf: (referenceId: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post(`/references/${referenceId}/upload-pdf`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  ingestPdf: (referenceId: string) =>
    api.post(`/references/${referenceId}/ingest-pdf`, null),
  attachToPaper: (referenceId: string, paperId: string) => {
    const fd = new FormData()
    fd.append('paper_id', paperId)
    return api.post(`/references/${referenceId}/attach-to-paper`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  delete: (referenceId: string) => api.delete(`/references/${referenceId}`)
  ,
}

// Conversion API - STUB: /convert/rich-to-latex and /convert/latex-to-rich
// endpoints do not exist in the backend yet. These are placeholders.
export const conversionAPI = {} as Record<string, never>

// Snapshots API - Document history feature
export interface Snapshot {
  id: string
  paper_id: string
  snapshot_type: 'auto' | 'manual' | 'restore'
  label: string | null
  created_by: string | null
  created_at: string
  sequence_number: number
  text_length: number | null
}

export interface SnapshotDetail extends Snapshot {
  materialized_text: string | null
  materialized_files: Record<string, string> | null
}

export interface SnapshotListResponse {
  snapshots: Snapshot[]
  total: number
  has_more: boolean
}

export interface DiffLine {
  type: 'added' | 'deleted' | 'unchanged'
  content: string
  line_number: number | null
}

export interface DiffStats {
  additions: number
  deletions: number
  unchanged: number
}

export interface SnapshotDiffResponse {
  from_snapshot: Snapshot
  to_snapshot: Snapshot
  diff_lines: DiffLine[]
  stats: DiffStats
}

export interface SnapshotRestoreResponse {
  message: string
  snapshot_id: string
  restored_at: string
}

export const snapshotsAPI = {
  // Create a manual snapshot
  createSnapshot: (paperId: string, label?: string) =>
    api.post<Snapshot>(`/papers/${paperId}/snapshots`, { label: label || null }),

  // List all snapshots for a paper
  listSnapshots: (paperId: string, options?: { skip?: number; limit?: number; snapshotType?: string }) =>
    api.get<SnapshotListResponse>(`/papers/${paperId}/snapshots`, {
      params: {
        skip: options?.skip ?? 0,
        limit: options?.limit ?? 50,
        snapshot_type: options?.snapshotType,
      },
    }),

  // Get a specific snapshot with content
  getSnapshot: (paperId: string, snapshotId: string) =>
    api.get<SnapshotDetail>(`/papers/${paperId}/snapshots/${snapshotId}`),

  // Update snapshot label
  updateSnapshotLabel: (paperId: string, snapshotId: string, label: string | null) =>
    api.put<Snapshot>(`/papers/${paperId}/snapshots/${snapshotId}`, { label }),

  // Delete a snapshot
  deleteSnapshot: (paperId: string, snapshotId: string) =>
    api.delete(`/papers/${paperId}/snapshots/${snapshotId}`),

  // Get diff between two snapshots
  getSnapshotDiff: (paperId: string, snapshotId1: string, snapshotId2: string) =>
    api.get<SnapshotDiffResponse>(`/papers/${paperId}/snapshots/${snapshotId1}/diff/${snapshotId2}`),

  // Get full-document diff between two snapshots (every line tagged, optionally for a specific file)
  getFullDiff: (paperId: string, snapshotId1: string, snapshotId2: string, file?: string) =>
    api.get<SnapshotDiffResponse>(`/papers/${paperId}/snapshots/${snapshotId1}/full-diff/${snapshotId2}${file ? `?file=${encodeURIComponent(file)}` : ''}`),

  // Restore document to a snapshot
  restoreSnapshot: (paperId: string, snapshotId: string) =>
    api.post<SnapshotRestoreResponse>(`/papers/${paperId}/snapshots/${snapshotId}/restore`),
}

// Zotero API
export const zoteroAPI = {
  getCollections: () =>
    api.get<{
      collections: Array<{ key: string; name: string; num_items: number }>
    }>('/zotero/collections'),

  getItems: (params?: { collection_key?: string; limit?: number }) =>
    api.get<{
      items: Array<{
        key: string
        title: string
        authors: string[]
        year: number | null
        doi: string | null
        journal: string | null
        abstract: string | null
        url: string | null
        item_type: string
        already_imported: boolean
      }>
      total: number
    }>('/zotero/items', { params }),

  importItems: (payload: { item_keys: string[]; project_id?: string }) =>
    api.post<{ imported: number; skipped: number; reference_ids: string[] }>(
      '/zotero/import',
      payload
    ),
}

// PDF Annotations API
import type {
  PdfAnnotationListResponse,
  PdfAnnotationCreate,
  PdfAnnotationUpdate,
  PdfAnnotation as PdfAnnotationType,
} from '../types'

export const annotationsAPI = {
  list: (documentId: string) =>
    api.get<PdfAnnotationListResponse>(`/documents/${documentId}/annotations`),

  create: (documentId: string, data: PdfAnnotationCreate) =>
    api.post<PdfAnnotationType>(`/documents/${documentId}/annotations`, data),

  update: (annotationId: string, data: PdfAnnotationUpdate) =>
    api.patch<PdfAnnotationType>(`/annotations/${annotationId}`, data),

  delete: (annotationId: string) =>
    api.delete(`/annotations/${annotationId}`),
}

// Subscription API
import type { SubscriptionTier, UserSubscription, UsageTracking } from '../types'

export const subscriptionAPI = {
  // Get current user's subscription and usage
  getMySubscription: () =>
    api.get<{
      subscription: UserSubscription | null
      usage: UsageTracking | null
      limits: Record<string, number>
    }>('/subscription/me'),

  // List available tiers
  listTiers: () =>
    api.get<{ tiers: SubscriptionTier[] }>('/subscription/tiers'),

  // Admin: change user tier
  changeTier: (userId: string, tierId: string) =>
    api.post<{ subscription: UserSubscription }>('/subscription/change-tier', {
      user_id: userId,
      tier_id: tierId,
    }),
}
